import { ReactNode } from "react";
import {
  Paper,
  Stack,
  useTheme,
  TextField,
  InputAdornment,
  capitalize,
  Typography,
} from "@mui/material";
import { Search } from "@mui/icons-material";
import {
  MenuOption,
  MoreOptionsButton,
} from "components/buttons/MoreOptionsButton";
import { useTranslation } from "react-i18next";
import { AUTURA_ASPHALT_LIGHT, TEXT_PRIMARY } from "context/themeContext";
import { DatePicker } from "@mui/x-date-pickers";
import { Dayjs } from "dayjs";
export interface MobileListSearchBarProps {
  menuOptions?: MenuOption[];
  searchQuery: string;
  setSearchQuery: (searchQuery: string) => void;
  startDate?: Dayjs | null;
  endDate?: Dayjs | null;
  setStartDate?: (date: Dayjs | null) => void;
  setEndDate?: (date: Dayjs | null) => void;
  heightOffset?: number;
}

const MobileListSearchBar = ({
  menuOptions,
  searchQuery,
  setSearchQuery,
  startDate,
  endDate,
  setStartDate,
  setEndDate,
  heightOffset = 0,
}: MobileListSearchBarProps): ReactNode => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { palette } = useTheme();
  return (
    <Paper
      sx={{
        position: "absolute",
        top: 150 + heightOffset,
        left: 0,
        right: 0,
        height: setStartDate && setEndDate ? 120 : 60,
        display: "flex",
        flexDirection: "column",
        zIndex: theme.zIndex.appBar - 1,
        borderBottom: "2px solid",
        borderRadius: 0,
        backgroundColor: AUTURA_ASPHALT_LIGHT,
      }}
    >
      <Stack
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          pl: 2,
          pr: 2,
          height: 60,
        }}
      >
        <TextField
          placeholder={capitalize(t("search"))}
          variant="outlined"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{
            flexGrow: 1,
            backgroundColor: "white",
            borderRadius: 2,
            border: "2px solid",
            borderColor: AUTURA_ASPHALT_LIGHT,
            height: 44,
          }}
          fullWidth
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Search />
              </InputAdornment>
            ),
          }}
          margin="none"
          size="small"
        />

        {menuOptions && menuOptions.length > 0 && (
          <MoreOptionsButton menuOptions={menuOptions} color={TEXT_PRIMARY} />
        )}
      </Stack>
      {setStartDate && setEndDate && (
        <Stack
        spacing={1}
        direction={'row'}
        sx={{ px: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.5 }}
      >
        <DatePicker
          disableFuture
          value={startDate}
          onChange={setStartDate}
          views={['year', 'month', 'day']}
          slotProps={{
            textField: {
              color: 'primary',
              placeholder: t('select', { name: t('date') }),
              label: '',
              margin: 'none',
              variant: 'standard',
              sx: {
                backgroundColor: 'white',
                borderRadius: '4px',
                border: '2px solid rgb(144, 144, 144)',
                height: 42,
              },
              InputProps: {
                sx: {
                  py: '5px',
                  px: 1,
                },
                disableUnderline: true,
              },
            },
          }}
        />
        <Typography variant='caption' color={palette.secondary.contrastText}>
          {t('to')}
        </Typography>
        <DatePicker
          label={''}
          disableFuture
          value={endDate}
          onChange={setEndDate}
          slotProps={{
            textField: {
              color: 'primary',
              placeholder: t('select', { name: t('date') }),
              label: '',
              margin: 'none',
              variant: 'standard',
              sx: {
                backgroundColor: 'white',
                borderRadius: '4px',
                border: '2px solid rgb(144, 144, 144)', //todo: we're breaking the onhover, if you have some free time, fix this
                height: 42,
              },
              InputProps: {
                sx: {
                  py: '5px',
                  px: 1,
                },
                disableUnderline: true,
              },
            },
          }}
        />
      </Stack>
      )}
    </Paper>
  );
};

export default MobileListSearchBar;
